import PropTypes from 'prop-types';
import React from 'react';
import Dropzone from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import FaPiedPiper from 'react-icons/lib/fa/pied-piper';
import styled from 'styled-components';
import { getImageUrl } from '~services/utils'; // eslint-disable-line

import logoDropzoneStyles from './dropzoneStyles';
import themeOptions from './themeOptions';
import portfolioBadgeMessages from './messages/PortFolioBadge_messages';
import messages from './messages/PaidFeatures_messages';

import Button from '../../components/Button';
import { Label, Textarea } from '../../components/Form';

const propTypes = {
  company: PropTypes.object,
  country: PropTypes.string.isRequired,
  editMode: PropTypes.bool.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleLogoUpload: PropTypes.func.isRequired,
  hidden: PropTypes.bool.isRequired,
  hidePortfolio: PropTypes.func.isRequired,
  identifier: PropTypes.string.isRequired,
  showPortfolio: PropTypes.func.isRequired,
};

const PortfolioCompany = ({
  company,
  country,
  editMode,
  handleChange,
  handleLogoUpload,
  hidden,
  hidePortfolio,
  identifier,
  showPortfolio,
}) => {
  const handleDrop = (acceptedFiles, rejectedFiles) => {
    if (acceptedFiles.length) {
      handleLogoUpload({
        country,
        identifier,
        file: acceptedFiles[0],
      });
    }

    if (rejectedFiles.length) {
      console.log('Wrong file type given.');
    }
  };

  const getThemeChanger = value => evnt => {
    evnt.preventDefault();
    if (editMode) {
      handleChange({ field: 'portfolio_theme', value });
    }
  }

  if (!company) return null;

  const { logo_id: logoId, about, portfolio_theme: theme } = company;
  const currentTheme = themeOptions.find(t => t.id === theme) || {};

  const { location: { origin } } = window;
  const brochureUrl = `${origin}/selvitys/esite/${country}/${identifier}`;

  return (
    <React.Fragment>
      <h2>
        <FormattedMessage
          id="portfolio.dashboard.title"
          description="Title for company dashboard brochure"
          defaultMessage="Company brochure"
        />
      </h2>

      <Display>
        <Url>{brochureUrl}</Url>
        <Button
          as="a"
          href={`/selvitys/esite/${country}/${identifier}`}
          kind="link"
          rel="noopener noreferrer"
          target="_blank"
        >
          <FormattedMessage
            {...portfolioBadgeMessages.callToActionButton}
            info="Check out brochure"
          />
        </Button>
      </Display>

      {hidden && (
        <VisibilityInfo plop>
          <FormattedMessage
            id="portfolio.dashboard.info.hidden"
            description="Info about brochure visibility"
            defaultMessage="Company brochure"
          />
          {editMode && (
            <Button className="toggle-portfolio-button" onClick={showPortfolio}>
              <FormattedMessage
                {...messages.showPortfolio}
                info="Button text"
              />
            </Button>
          )}
        </VisibilityInfo>
      )}
      {!hidden && (
        <VisibilityInfo>
          <FormattedMessage
            id="portfolio.dashboard.info.visible"
            description="Info about brochure visibility"
            defaultMessage="Company brochure"
          />
          {editMode && (
            <Button
              className="toggle-portfolio-button"
              color="red"
              onClick={hidePortfolio}
            >
              <FormattedMessage
                {...messages.hidePortfolio}
                info="Button text"
              />
            </Button>
          )}
        </VisibilityInfo>
      )}

      <CompanyInfo>
        <Dropzone
          onDrop={handleDrop}
          style={logoDropzoneStyles}
          accept={'image/jpg, image/jpeg, image/png, image/gif'}
          multiple={false}
          disabled={!editMode}
        >
          <CompanyLogo>
            {(!logoId && editMode) && (
              <React.Fragment>
                <AddIcon />
                <FormattedMessage
                  {...messages.addCompanyLogo}
                  info="Add company logo"
                />
              </React.Fragment>
            )}
            {!!logoId && (
              <Logo src={getImageUrl(logoId, false)} />
            )}
          </CompanyLogo>
        </Dropzone>

        <CompanyDescription>
          <Label>
            <FormattedMessage
              {...messages.companyInfoLabel}
              info="Info about the company"
            />
          </Label>
          <Description
            disabled={!editMode}
            rows="4"
            value={about}
            onChange={({ target: { value } }) =>
              handleChange({ field: 'about', value })
            }
          />
        </CompanyDescription>
      </CompanyInfo>

      <ThemeSelector>
        <Label as="span">
          <FormattedMessage
            {...messages.chooseThemeLabel}
            info="Choose theme label"
          />
          <CurrentTheme>
            {currentTheme.translation}
          </CurrentTheme>
        </Label>
        <ThemeOptions>
          {themeOptions.map(({ id, color }) => (
            <ThemeOption
              color={color}
              editMode={editMode}
              key={`theme_option_${id}`}
              onClick={getThemeChanger(id)}
              selected={id === theme}
            />
          ))}
        </ThemeOptions>
      </ThemeSelector>

    </React.Fragment>
  );
};

PortfolioCompany.propTypes = propTypes;

const AddIcon = styled(FaPiedPiper)`
  font-size: 20pt;
  margin-bottom: 6px;
`;

const Display = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const CompanyDescription = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin: 24px 0px;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const CompanyLogo = styled.div`
  align-items: center;
  box-sizing: border-box;
  color: ${props => props.theme['grey-normal']};
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: 12px;
  width: 100%;
`;

const CurrentTheme = styled.span`
  font-size: 10pt;
  font-weight: normal;
  margin-left: 6px;
`;

const Description = styled(Textarea)`
  &[disabled] {
    background-color: #ffffff;
    border-color: #ffffff;

    &:hover {
      cursor: default;
    }
  }
`;

const Logo = styled.img``;

const ThemeOption = styled.div`
  background-color: ${props => props.color};
  border: 3px solid ${props => props.selected ? '#000000' : '#ffffff'};
  border-radius: 6px;
  height: 36px;
  margin-right: 12px;
  width: 36px;

  &:hover {
    cursor: ${props => props.editMode ? 'pointer' : 'default'};
  }
`;

const ThemeOptions = styled.div`
  display: flex;
`;

const ThemeSelector = styled.div`
  margin: 24px 0px;
`;

const Url = styled.div`
  border: 1px solid ${props => props.theme['grey-normal']};
  border-radius: 6px;
  font-size: 10pt;
  margin: 0px 6px 6px 0px;
  max-width: 100%;
  overflow: hidden;
  padding: 6px 9px;
  white-space: nowrap;
`;

const VisibilityInfo = styled.div`
  font-weight: ${props => props.plop ? 'bold' : 'normal'};
`;

export default PortfolioCompany;
